<template>
  <div class="flex h-full overflow-hidden relative" style="background-color: #fcfcff">
    <div :class="leftColClass" class="overflow-hidden border-r border-stroke border-solid">
      <div class="border-r border-stroke w-20 flex-shrink-0 p-2 flex flex-col gap-x-3 gap-y-1 overflow-auto">
        <VDropdown
          v-for="item in buttonCategories"
          :key="item.key"
          ref="vdropdown"
          theme="flow"
          class="inline"
          placement="right-start"
          :distance="3"
          :triggers="['click']"
          auto-hide
          @apply-show="onDropdownApplyShow(item)"
          @apply-hide="onDropdownApplyHide(item)"
        >
          <div
            class="text-10 flex items-center justify-center flex-col cursor-pointer select-none hover:bg-gray-200 rounded-4 text-center px-1 p-2"
            :class="{ 'bg-gray-200': activeCategoryDropdown === item.key }"
          >
            <Icon v-if="item.icon" :name="item.icon" class="w-6 h-6 mb-1" :class="{ [item.color]: true }" />
            <span class="font-500">{{ item.name }}</span>
          </div>
          <template #popper>
            <div class="w-200 px-2 py-2" style="background-color: #fcfcff">
              <div
                v-for="button in getButtons(item.key)"
                :key="button.key"
                draggable="true"
                class="px-4 rounded-4 pt-2 pb-2 font-500 text-12 flex gap-x-2 cursor-pointer hover:cursor-grab select-none tracking-wide text-semi-dark-text hover:bg-gray-200"
                @dragstart="onButtonDragStart($event, button.key)"
                @click="onButtonDblClick(button.key)"
              >
                <Icon :name="button.icon" class="w-5 h-5 flex-shrink-0" :class="{ [item.color]: true }" />
                <div class="flex items-center">{{ $t(`evaluate_editor.${button.key}`) }}</div>
              </div>
            </div>
          </template>
        </VDropdown>
      </div>
      <div class="w-full flex flex-col overflow-auto">
        <div class="bg-white py-2 h-12 shadow-card top-0 flex items-center pl-3 pr-5 mb-1 border-stroke border-solid border-b">
          <span class="text-13 font-500 mr-auto">{{ $t('evaluate.actions') }}:</span>
          <PillButton v-if="selectedItemIds.length" :text="$t('delete')" small outlined class="mr-2" @click="onDeleteClick" />
          <PillButton v-if="selectedItemIds.length" :text="$t('flow.copy_to')" small outlined @click="onCopyToClick" />
          <div v-if="data.length" class="flex items-center ml-5">
            <span class="mr-2 text-13 font-500 mt-0.5">{{ $t('select_all') }}</span>
            <Checkbox v-model="selectAll" @update:modelValue="onEvaluateSelectAll" />
          </div>
        </div>
        <div class="flex-grow overflow-auto">
          <template v-if="evaluate.length">
            <div class="pl-3 pr-2" v-for="(evaluateItem, index) in evaluate" :key="evaluateItem.id + index">
              <EvaluateDropZone v-if="index === 0" :evaluate="evaluate" :index="index" @evaluate-update="onDrop" />
              <EvaluateItem
                :invalid="!getValidity(evaluateItem.id)"
                :selectedEvaluateItemId="selectedEvaluateItemId"
                :evaluate="evaluate"
                :item="evaluateItem"
                selectable
                :selected="isSelected(evaluateItem)"
                @update="onEvaluateItemUpdated($event, index)"
                @item-click="onItemClicked"
                @evaluate-update="$emit('update', $event)"
                @select="onEvaluateItemSelect"
                @unselect="onEvaluateItemUnSelect"
              />
              <EvaluateDropZone :evaluate="evaluate" :index="index + 1" @evaluate-update="onDrop" />
            </div>
          </template>
          <template v-else>
            <EvaluateDropZone class="!h-20" :evaluate="evaluate" :index="0" @evaluate-update="onDrop" />
          </template>
        </div>
      </div>
    </div>
    <div :class="rightColClass">
      <template v-if="selectedEvaluateItem">
        <div class="px-6 py-2 h-12 flex justify-between flex-shrink-0 shadow-card items-center border-stroke border-solid border-b">
          <div class="flex items-center">
            <IconButton icon="chevron_left" class="-ml-4 lg:hidden" @click="selectedEvaluateItem = null" />
            <span class="text-16 flex-grow capitalize">{{ getEvaluateTitle(selectedEvaluateItem.type) }}</span>
          </div>
          <div>
            <PillButton v-if="canBlockPreview(selectedEvaluateItem)" text="Preview" outlined @click="onPreviewBlock(evaluate)" />
          </div>
        </div>
        <AgentToolDefinitionSection
          v-if="selectedEvaluateItemIsAgentTool && selectedEvaluateItem.type !== 'data-adapter'"
          class="mx-3 mt-3 flex-shrink-0"
          :tool-description="selectedEvaluateItem.tool_description || ''"
          :policy="selectedEvaluateItem.policy || ''"
          :tool-type="selectedEvaluateItem.type"
          @update="onToolDefinitionUpdated"
        />
        <div class="flex-grow min-h-0">
          <component :is="configComponent" :item="selectedEvaluateItem" :is-agent="selectedEvaluateItemIsAgentTool" @valid="onValidChange" @update="onSelectedEvaluateItemUpdated" />
        </div>
        <div class="bg-white px-3 pt-2 mt-px z-1 h-full w-1/2 mb-7 top-14 absolute transition-all duration-300" :class="{ '-right-1/2': !isPreviewing, 'right-0': isPreviewing }">
          <PillButton text="Close" outlined @click="onClosePreview" />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import uuidv4 from '@/helpers/uuid';
import { removeEvaluateItem, updateEvaluateItem } from '@/helpers/flow';
import DeleteModal from '../../modals/DeleteModal.vue';
import EvaluateDropZone from './EvaluateDropZone';
import EvaluateCopyModal from './EvaluateCopyModal';
import EvaluateItems, { Categories } from './EvaluateItems';
import { createEvaluateItem } from './EvaluateItemsConfig';
import AgentToolDefinitionSection from './AgentToolDefinitionSection.vue';

export default {
  name: 'EvaluateEditor',
  components: {
    EvaluateDropZone,
    AgentToolDefinitionSection,
  },
  props: {
    data: {
      type: Array,
    },
    hiddenEvaluateTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isPreviewing: false,
      evaluate: null,
      selectedEvaluateItem: null,
      selectAll: false,
      validity: {},
      selectedItemId: null,
      selectedItemIds: [],
      activeCategoryDropdown: null,
    };
  },
  computed: {
    buttonCategories() {
      return Categories.map((category) => {
        return {
          ...category,
          name: this.$t(`evaluate_editor.${category.key}`),
        };
      });
    },
    configComponent() {
      return EvaluateItems[this.selectedEvaluateItem.type]?.configComponent || null;
    },
    leftColClass() {
      const classes = ['flex flex-row flex-grow xl:max-w-1/3 xxl:max-w-1/2'];
      if (this.selectedEvaluateItem) {
        classes.push('hidden xl:flex');
      }
      return classes;
    },
    rightColClass() {
      const classes = ['flex-grow xl:max-w-2/3 xxl:max-w-1/2 border-l border-stroke border-solid '];
      if (this.selectedEvaluateItem) {
        classes.push('flex flex-col');
      } else {
        classes.push('hidden xl:flex');
      }
      return classes;
    },
    selectedEvaluateItemId() {
      return this.selectedEvaluateItem?.id;
    },
    selectedEvaluateItemIsAgentTool() {
      return this.isAgentTool(this.evaluate, this.selectedEvaluateItemId);
    },
  },
  methods: {
    isAgentTool(items, id) {
      if (!id || !Array.isArray(items)) return false;
      return items.some((item) => {
        if (item.type === 'ai-agent' && Array.isArray(item.payload?.tools) && item.payload.tools.some((tool) => tool.id === id)) return true;
        if (item.type === 'ai-agent' && this.isAgentTool(item.payload?.tools, id)) return true;
        if (item.type === 'logic-condition') {
          return (
            this.isAgentTool(item.payload?.expressions, id) || item.payload?.expressions?.some((expression) => this.isAgentTool(expression.payload, id)) || this.isAgentTool(item.payload?.else, id)
          );
        }
        if (item.type === 'logic-parallel') return this.isAgentTool(item.payload, id);
        return false;
      });
    },
    onClosePreview() {
      this.isPreviewing = false;
    },
    canBlockPreview(item) {
      return ['action-execute-api'].includes(item.type);
    },
    onPreviewBlock(item) {
      console.log(item[0]); // eslint-disable-line
      this.isPreviewing = true;
    },
    onButtonDblClick(type) {
      this.addEvaluation(createEvaluateItem(type));
      this.$refs.vdropdown.forEach((dd) => dd.hide());
    },
    onButtonDragStart(e, type) {
      const item = createEvaluateItem(type);
      e.dataTransfer.setData('id', item.id);
      e.dataTransfer.setData('item', JSON.stringify(item));

      const component = EvaluateItems[type]?.component || null;
      e.dataTransfer.setData('type', component.name);
    },
    getButtons(category) {
      return Object.values(EvaluateItems).filter((item) => item.category === category);
    },
    onDropdownApplyShow(item) {
      this.activeCategoryDropdown = item.key;
    },
    onDropdownApplyHide() {
      this.activeCategoryDropdown = null;
    },
    onDeleteClick() {
      this.$showModal(DeleteModal, {
        subtitle: this.$t('evaluate.confirm_remove_items_message'),
        onConfirm: () => {
          this.selectedItemIds.forEach((id) => {
            this.$emit('unselect', id);
            removeEvaluateItem(this.evaluate, id);
          });
          this.selectedEvaluateItem = null;
          this.selectedItemIds = [];
          this.selectAll = false;
          this.updateEvaluatePayload();
        },
      });
    },
    onCopyToClick() {
      this.$showModal(
        EvaluateCopyModal,
        { selectedItemIds: this.selectedItemIds, evaluateData: this.data },
        {
          addToCurrent: (items) => {
            items.forEach((item) => {
              this.addEvaluation(item);
            });
          },
        },
      );
    },
    onEvaluateItemSelect(id) {
      if (this.selectedItemIds.includes(id)) {
        this.selectedItemIds = this.selectedItemIds.filter((item) => item !== id);
      } else {
        this.selectedItemIds.push(id);
      }

      if (this.selectedItemIds.length !== this.data.length) {
        this.selectAll = false;
      } else if (this.selectedItemIds.length === this.data.length) {
        this.selectAll = true;
      }
    },
    onEvaluateItemUnSelect(id) {
      if (this.selectedItemIds.includes(id)) {
        this.selectedItemIds = this.selectedItemIds.filter((item) => item !== id);
      }
    },
    onEvaluateSelectAll() {
      if (this.selectAll) {
        this.selectedItemIds = this.data.map((item) => item.id);
      } else {
        this.selectedItemIds = [];
      }
    },
    getButtonIcon(name) {
      return EvaluateItems[name]?.icon || '';
    },
    getValidity(id) {
      return id in this.validity ? this.validity[id] : true;
    },
    onValidChange(valid) {
      this.validity[this.selectedEvaluateItemId] = valid;
    },
    isSelected(evaluateItem) {
      return this.selectedItemIds.includes(evaluateItem.id);
    },
    onDrop($event, evaluateItem) {
      this.$refs.vdropdown.forEach((dd) => {
        dd.hide();
      });
      this.onItemClicked(null, evaluateItem);
      this.$emit('update', $event);
    },
    getEvaluateTitle(type) {
      return this.$t(`evaluate_editor.${type}`);
    },
    onToolDefinitionUpdated(toolDef) {
      const updated = { ...this.selectedEvaluateItem, ...toolDef };
      this.selectedEvaluateItem = updated;
      updateEvaluateItem(this.evaluate, updated);
      this.updateEvaluatePayload();
    },
    onSelectedEvaluateItemUpdated(item) {
      updateEvaluateItem(this.evaluate, item);
      this.updateEvaluatePayload();
    },
    onEvaluateItemUpdated(evaluateItem, index) {
      this.evaluate[index] = evaluateItem;
      this.updateEvaluatePayload();
    },
    onItemClicked($event, evaluateItem) {
      if (evaluateItem.id === this.selectedEvaluateItemId) {
        this.selectedEvaluateItem = null;
      } else {
        this.selectedEvaluateItem = null;
        this.$nextTick(() => {
          this.selectedEvaluateItem = evaluateItem;
        });
      }
      this.$emit('item-clicked', evaluateItem);
    },
    updateEvaluation(evaluation) {
      const index = this.evaluate.findIndex((i) => i.payload.id === evaluation.id);
      this.evaluate[index].payload = evaluation;
      this.updateEvaluatePayload();
    },
    addEvaluation(evaluation) {
      evaluation.id = uuidv4();
      this.evaluate.push(evaluation);
      this.updateEvaluatePayload();
      this.onItemClicked(null, evaluation);
    },
    updateEvaluatePayload() {
      this.$emit('update', this.evaluate);
    },
  },
  watch: {
    data: {
      handler() {
        if (this.data) {
          this.evaluate = JSON.parse(JSON.stringify(this.data));
        }
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>
